// The numbers behind a profile along a bent line: everything is "unfolded" onto s = metres along the line from
// its first point, and z = elevation.  Passages within the corridor are swung onto the line at right angles.
import * as THREE from 'three';

// ------------------------------------------------------------------ the line
export class Polyline {
  constructor(pts) {
    this.pts = pts; this.seg = []; let s = 0;
    for (let k = 0; k < pts.length - 1; k++) {
      const [ax, ay] = pts[k], [bx, by] = pts[k + 1], L = Math.hypot(bx - ax, by - ay);
      this.seg.push({ ax, ay, ux: L ? (bx - ax) / L : 0, uy: L ? (by - ay) / L : 0, L, s0: s }); s += L;
    }
    this.L = s;
    this.corners = this.seg.map(g => g.s0).concat([s]);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const [x, y] of pts) { x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y); }
    this.box = [x0, y0, x1, y1];
  }

  // nearest point on the line: {s, d (distance, m), side (+1 right of the line, -1 left)}; null past the two ends
  project(E, N) {
    let best = null;
    const last = this.seg.length - 1;
    this.seg.forEach((g, k) => {
      const dx = E - g.ax, dy = N - g.ay; let t = dx * g.ux + dy * g.uy;
      if ((k === 0 && t < -0.5) || (k === last && t > g.L + 0.5)) return;
      t = Math.max(0, Math.min(g.L, t));
      const px = g.ax + g.ux * t, py = g.ay + g.uy * t, d = Math.hypot(E - px, N - py);
      if (!best || d < best.d) best = { s: g.s0 + t, d, side: (dx * g.uy - dy * g.ux) >= 0 ? 1 : -1 };
    });
    return best;
  }

  // easting, northing and bearing (deg) at s
  at(s) {
    s = Math.max(0, Math.min(this.L, s));
    let g = this.seg[this.seg.length - 1];
    for (const x of this.seg) if (s <= x.s0 + x.L) { g = x; break; }
    const t = s - g.s0;
    return { E: g.ax + g.ux * t, N: g.ay + g.uy * t, brg: ((Math.atan2(g.ux, g.uy) * 180 / Math.PI) + 360) % 360 };
  }

  bearings() { return this.seg.map(g => ((Math.atan2(g.ux, g.uy) * 180 / Math.PI) + 360) % 360); }
}

const hex = (r, g, b) => '#' + new THREE.Color(r, g, b).getHexString();
const quant = (c) => Math.round(c * 24) / 24;          // fewer distinct colours -> fewer SVG paths

// ------------------------------------------------------------------ passages near the line
// v = the Viewer; colour = 'view' (as coloured in the 3D view) or 'cave'
export function passagesNear(v, line, corridor, colour = 'view') {
  const out = { quads: [], cl: [], stations: [], caves: new Map() };
  if (!v.M || !v.tpos) return out;
  const M = v.M, [xc, yc, zc] = M.origin, sh = v.opts.shift, ns = v.tns, P = v.tpos, C = v.tcol;
  const [bx0, by0, bx1, by1] = line.box, pad = corridor + 30;
  const inBox = (E, N) => E > bx0 - pad && E < bx1 + pad && N > by0 - pad && N < by1 + pad;
  const vis = v.vis, hide = v.filter.mode === 'hide';
  const caveOf = (si) => M.surveys[si]?.cave_id;
  const caveCol = (id) => { const c = v.C?.caves?.get(id); return c ? '#' + c.getHexString() : '#f07d4c'; };
  // ring centres, tops and bottoms (computed lazily per ring)
  const nr = P.length / 3 / ns, ring = new Map();
  const ringAt = (r) => {
    let o = ring.get(r); if (o !== undefined) return o;
    let x = 0, z = 0, lo = Infinity, hi = -Infinity, cr = 0, cg = 0, cb = 0;
    for (let j = 0; j < ns; j++) {
      const q = 3 * (r * ns + j); x += P[q]; z += P[q + 2]; lo = Math.min(lo, P[q + 1]); hi = Math.max(hi, P[q + 1]);
      if (C) { cr += C[q]; cg += C[q + 1]; cb += C[q + 2]; }
    }
    const E = x / ns + xc, N = yc - z / ns;
    o = null;
    if (inBox(E, N)) {
      const p = line.project(E, N);
      if (p && p.d <= corridor) o = { s: p.s, d: p.d, E, N, top: hi + zc + sh, bot: lo + zc + sh, rgb: [cr / ns, cg / ns, cb / ns] };
    }
    ring.set(r, o); return o;
  };
  for (const r of v.tpairs) {
    if (r + 1 >= nr) continue;
    const si = v.rsurv[r];
    if (vis && !vis[si] && hide) continue;
    const a = ringAt(r); if (!a) continue;
    const b = ringAt(r + 1); if (!b) continue;
    // both ends must unfold consistently (not across a bend of the line onto two different legs)
    if (Math.abs(a.s - b.s) > Math.hypot(a.E - b.E, a.N - b.N) + 2) continue;
    const cave = caveOf(si);
    const col = colour === 'cave' ? caveCol(cave) : hex(quant((a.rgb[0] + b.rgb[0]) / 2), quant((a.rgb[1] + b.rgb[1]) / 2), quant((a.rgb[2] + b.rgb[2]) / 2));
    out.quads.push({ s0: a.s, t0: a.top, b0: a.bot, s1: b.s, t1: b.top, b1: b.bot, d: Math.max(a.d, b.d), col, cave, dim: vis && !vis[si] });
    let c = out.caves.get(cave);
    if (!c) { c = { id: cave, name: M.caves.find(x => x.id === cave)?.name || 'Cave', color: caveCol(cave), s0: Infinity, s1: -Infinity, top: -Infinity, len: 0 }; out.caves.set(cave, c); }
    c.s0 = Math.min(c.s0, a.s, b.s); c.s1 = Math.max(c.s1, a.s, b.s); c.top = Math.max(c.top, a.top, b.top); c.len += Math.abs(a.s - b.s);
  }
  // centrelines and stations
  const S = v.stPos, st = new Map();
  const stAt = (i) => {
    let o = st.get(i); if (o !== undefined) return o;
    const E = S[3 * i] + xc, N = yc - S[3 * i + 2], z = S[3 * i + 1] + zc + sh;
    o = null;
    if (inBox(E, N)) { const p = line.project(E, N); if (p && p.d <= corridor) o = { s: p.s, d: p.d, sd: p.d * p.side, z, i, E, N }; }
    st.set(i, o); return o;
  };
  for (let k = 0; k < v.segSurvey.length; k++) {
    const si = v.segSurvey[k]; if (vis && !vis[si] && hide) continue;
    const a = stAt(v.segs[2 * k]), b = stAt(v.segs[2 * k + 1]);
    if (!a || !b) continue;
    if (Math.abs(a.s - b.s) > Math.hypot(a.E - b.E, a.N - b.N) + 2) continue;
    out.cl.push([a.s, a.z, b.s, b.z, Math.max(a.d, b.d)]);
  }
  for (const o of st.values()) if (o) out.stations.push({ ...o, name: v.names[o.i] });
  out.caves = [...out.caves.values()].filter(c => c.len > 0).sort((a, b) => b.len - a.len);
  return out;
}

// ------------------------------------------------------------------ the plan along the line
// The plan unfolded onto the line: s along it, d metres to its right (+) or left (-).  Passage footprints (each pair
// of tube rings as one outline), centrelines, and the drawn maps' walls and detail, within `half` metres of the line.
const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
function hull(P) {
  P = P.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (P.length < 3) return P;
  const lo = [], hi = [];
  for (const p of P) { while (lo.length > 1 && cross(lo[lo.length - 2], lo[lo.length - 1], p) <= 0) lo.pop(); lo.push(p); }
  for (let i = P.length - 1; i >= 0; i--) { const p = P[i]; while (hi.length > 1 && cross(hi[hi.length - 2], hi[hi.length - 1], p) <= 0) hi.pop(); hi.push(p); }
  return lo.slice(0, -1).concat(hi.slice(0, -1));
}

export function planNear(v, line, half, colour = 'view') {
  const out = { polys: [], cl: [], walls: [], detail: [], texts: [] };
  if (!v.M || !v.tpos) return out;
  const M = v.M, [xc, yc] = M.origin, ns = v.tns, P = v.tpos, C = v.tcol;
  const [bx0, by0, bx1, by1] = line.box, pad = half + 30;
  const inBox = (E, N) => E > bx0 - pad && E < bx1 + pad && N > by0 - pad && N < by1 + pad;
  const proj = (E, N) => { if (!inBox(E, N)) return null; const p = line.project(E, N); return p && p.d <= half ? [p.s, p.d * p.side] : null; };
  const ok = (a, b, dist) => Math.abs(a[0] - b[0]) <= dist + 2;           // not torn across a bend of the line
  const vis = v.vis, hide = v.filter.mode === 'hide';
  const caveCol = (id) => { const c = v.C?.caves?.get(id); return c ? '#' + c.getHexString() : '#f07d4c'; };
  const nr = P.length / 3 / ns, rings = new Map();
  const ringAt = (r) => {
    let o = rings.get(r); if (o !== undefined) return o;
    o = null;
    const q0 = 3 * r * ns, E0 = P[q0] + xc, N0 = yc - P[q0 + 2];
    if (inBox(E0, N0)) {
      const pts = []; let cr = 0, cg = 0, cb = 0, E = 0, N = 0;
      for (let j = 0; j < ns; j++) {
        const q = 3 * (r * ns + j), e = P[q] + xc, n = yc - P[q + 2], p = line.project(e, n);
        if (!p || p.d > half) { o = null; rings.set(r, o); return o; }
        pts.push([p.s, p.d * p.side]); E += e; N += n;
        if (C) { cr += C[q]; cg += C[q + 1]; cb += C[q + 2]; }
      }
      o = { pts, E: E / ns, N: N / ns, rgb: [cr / ns, cg / ns, cb / ns] };
    }
    rings.set(r, o); return o;
  };
  for (const r of v.tpairs) {
    if (r + 1 >= nr) continue;
    const si = v.rsurv[r];
    if (vis && !vis[si] && hide) continue;
    const a = ringAt(r); if (!a) continue;
    const b = ringAt(r + 1); if (!b) continue;
    const dist = Math.hypot(a.E - b.E, a.N - b.N);
    if (Math.abs(a.pts[0][0] - b.pts[0][0]) > dist + 4) continue;
    const cave = M.surveys[si]?.cave_id;
    const col = colour === 'cave' ? caveCol(cave) : hex(quant((a.rgb[0] + b.rgb[0]) / 2), quant((a.rgb[1] + b.rgb[1]) / 2), quant((a.rgb[2] + b.rgb[2]) / 2));
    out.polys.push({ pts: hull(a.pts.concat(b.pts)), col, dim: vis && !vis[si] });
  }
  const S = v.stPos;
  for (let k = 0; k < v.segSurvey.length; k++) {
    const si = v.segSurvey[k]; if (vis && !vis[si] && hide) continue;
    const i = v.segs[2 * k], j = v.segs[2 * k + 1];
    const Ea = S[3 * i] + xc, Na = yc - S[3 * i + 2], Eb = S[3 * j] + xc, Nb = yc - S[3 * j + 2];
    const a = proj(Ea, Na), b = proj(Eb, Nb);
    if (a && b && ok(a, b, Math.hypot(Ea - Eb, Na - Nb))) out.cl.push([a[0], a[1], b[0], b[1]]);
  }
  for (const [k, l] of [['walls', v.vmapLines?.[0]], ['detail', v.vmapLines?.[1]]]) {
    if (!l) continue;
    const pos = l.userData.pos, idx = l.geometry.index?.array; if (!idx) continue;
    const cache = new Map();
    const at = (n) => { let o = cache.get(n); if (o === undefined) { o = proj(pos[3 * n] + xc, yc - pos[3 * n + 2]); cache.set(n, o); } return o; };
    for (let m = 0; m < idx.length; m += 2) {
      const i = idx[m], j = idx[m + 1];
      const Ea = pos[3 * i] + xc, Na = yc - pos[3 * i + 2];
      if (!inBox(Ea, Na)) continue;
      const a = at(i); if (!a) continue;
      const b = at(j); if (!b) continue;
      if (ok(a, b, Math.hypot(Ea - pos[3 * j] - xc, Na - yc + pos[3 * j + 2]))) out[k].push([a[0], a[1], b[0], b[1]]);
    }
  }
  for (const t of M.vmap?.labels || []) {
    const p = proj(t.p[0] + xc, yc - t.p[2]);
    if (p) out.texts.push({ s: p[0], d: p[1], z: t.p[1] + M.origin[2] + (v.opts.shift || 0), text: t.t, kind: t.k });
  }
  return out;
}

// Everything near the line a label can be made from: {kind, text, s, d (m right of the line), z (m)}
export function featuresNear(v, line, half, { places = [], cavePlaces = [], readings = [], groundAt } = {}) {
  const out = [];
  if (!v.M) return out;
  const [xc, yc, zc] = v.M.origin, sh = v.opts.shift || 0;
  const put = (kind, text, E, N, z) => {
    if (!text || E == null || N == null || !Number.isFinite(z)) return;
    const p = line.project(E, N); if (!p || p.d > half) return;
    out.push({ kind, text: String(text), s: p.s, d: p.d * p.side, z });
  };
  for (const p of places) {
    if (p.loc?.easting == null) continue;
    const z = p.loc.on_surface === false && p.loc.elevation != null ? p.loc.elevation : (p.loc.ground ?? p.loc.elevation ?? groundAt(p.loc.easting, p.loc.northing));
    put(p.kind === 'entrance' ? 'entrance' : p.type || 'feature', p.name, p.loc.easting, p.loc.northing, z);
  }
  const S = v.stPos;
  for (const c of cavePlaces) {
    const idx = (c.stations || []).map(n => v.nameIdx?.get(n)).filter(i => i != null);
    if (!idx.length) continue;
    let E = 0, N = 0, Z = 0;
    for (const i of idx) { E += S[3 * i] + xc; N += yc - S[3 * i + 2]; Z += S[3 * i + 1] + zc + sh; }
    put('place', c.label || c.name, E / idx.length, N / idx.length, Z / idx.length);
  }
  for (const t of v.M.vmap?.labels || []) put(t.k === 'lead' ? 'lead' : 'maptext', t.t, t.p[0] + xc, yc - t.p[2], t.p[1] + zc + sh);
  for (const m of readings) put(m.k, `${m.k === 'height' ? 'Ceiling' : 'Pit'} ${m.v} ft`, m.e, m.n, m.z + sh);
  return out;
}

// entrances (and other surface features of kind entrance) near the line
export function entrancesNear(places, line, corridor, groundAt) {
  const out = [];
  for (const p of places || []) {
    if (p.type !== 'feature' || p.kind !== 'entrance' || p.loc?.easting == null) continue;
    const q = line.project(p.loc.easting, p.loc.northing);
    if (!q || q.d > Math.max(corridor, 40)) continue;
    const z = p.loc.on_surface === false && p.loc.elevation != null ? p.loc.elevation : (p.loc.ground ?? p.loc.elevation ?? groundAt(p.loc.easting, p.loc.northing));
    out.push({ s: q.s, d: q.d, sd: q.d * q.side, z, name: p.name, id: p.id });
  }
  return out;
}

// ------------------------------------------------------------------ geology along the line
// A raster of the formation under the ground along the line, as a PNG data URL, plus where to label each unit.
export function strataImage(geo, line, groundS, groundZ, zlo, zhi, colors, opts = {}) {
  const s0 = opts.s0 ?? 0, s1 = opts.s1 ?? line.L;
  const W = Math.max(16, Math.min(2400, Math.round(opts.W || (s1 - s0) / 1.5)));
  const H = Math.max(16, Math.min(1400, Math.round(opts.H || (zhi - zlo) / 0.75)));
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const g2 = cv.getContext('2d'), img = g2.createImageData(W, H), d = img.data;
  const idx = new Int16Array(W * H).fill(-1);
  const gAt = (s) => {                              // ground elevation at s (linear in the sampled profile)
    let lo = 0, hi = groundS.length - 1;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; if (groundS[m] <= s) lo = m; else hi = m; }
    const t = (s - groundS[lo]) / ((groundS[hi] - groundS[lo]) || 1), a = groundZ[lo], b = groundZ[hi];
    return a == null || b == null ? (a ?? b ?? zlo) : a + (b - a) * Math.max(0, Math.min(1, t));
  };
  for (let c = 0; c < W; c++) {
    const s = s0 + (c + 0.5) / W * (s1 - s0), { E, N } = line.at(s), gz = gAt(s);
    for (let r = 0; r < H; r++) {
      const Z = zhi - (r + 0.5) / H * (zhi - zlo);
      if (Z > gz) continue;
      idx[r * W + c] = geo.unitAt(E, N, Z);
    }
  }
  const rgb = colors.map(c => [1, 3, 5].map(i => parseInt(c.slice(i, i + 2), 16)));
  const best = new Map();                          // unit -> {c, r0, r1} thickest run (for its label)
  for (let c = 0; c < W; c++) {
    let run = -1, r0 = 0;
    for (let r = 0; r <= H; r++) {
      const k = r < H ? idx[r * W + c] : -2;
      if (k !== run) {
        if (run >= 0) { const b = best.get(run); if (!b || r - r0 > b.r1 - b.r0) best.set(run, { c, r0, r1: r }); }
        run = k; r0 = r;
      }
    }
  }
  for (let r = 0; r < H; r++) for (let c = 0; c < W; c++) {
    const p = r * W + c, k = idx[p], o = 4 * p;
    if (k < 0) continue;
    const edge = (c > 0 && idx[p - 1] >= 0 && idx[p - 1] !== k) || (r > 0 && idx[p - W] >= 0 && idx[p - W] !== k);
    const f = edge ? 0.45 : 1, q = rgb[k] || [128, 128, 128];
    d[o] = q[0] * f; d[o + 1] = q[1] * f; d[o + 2] = q[2] * f; d[o + 3] = 255;
  }
  g2.putImageData(img, 0, 0);
  const labels = [...best.entries()].map(([k, b]) => ({ unit: k, s: s0 + (b.c + 0.5) / W * (s1 - s0),
    z: zhi - ((b.r0 + b.r1) / 2) / H * (zhi - zlo), thick: (b.r1 - b.r0) / H * (zhi - zlo) }));
  const present = [...new Set(idx)].filter(k => k >= 0).sort((a, b) => b - a);
  return { url: cv.toDataURL('image/png'), s0, s1, zlo, zhi, labels, present };
}
